import { debounce } from "lodash"
import { useEffect, useLayoutEffect, useMemo, useState } from "react"

/**
 * Measures the size of an element returning the width and height. Will update when the element is resized via a ResizeObserver.
 * @param elementRef - Reference to the element to measure
 * @param debounceMs - Optional debounce delay in milliseconds for resize updates
 */

export const useElementSize = (
  elementRef: React.RefObject<HTMLElement | null>,
  debounceMs = 0
) => {
  const [size, setSize] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  })

  // Measure before paint, so the first consumer render is not 0x0.
  useLayoutEffect(() => {
    const element = elementRef?.current
    if (!element) return
    const { width, height } = element.getBoundingClientRect()
    setSize({ width, height })
  }, [elementRef])

  const debouncedSetSize = useMemo(
    () => debounce(setSize, debounceMs),
    [debounceMs]
  )

  useEffect(() => {
    const element = elementRef?.current
    if (!element) return

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (entry) {
        const { width, height } = entry.contentRect
        debouncedSetSize({ width, height })
      }
    })

    resizeObserver.observe(element)

    return () => {
      resizeObserver.disconnect()
      debouncedSetSize.cancel?.()
    }
  }, [elementRef, debouncedSetSize])

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        const element = elementRef.current
        if (element) {
          const { width, height } = element.getBoundingClientRect()
          debouncedSetSize({ width, height })
        }
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange)
      debouncedSetSize.cancel?.()
    }
  }, [elementRef, debouncedSetSize])

  return size
}
